<template>
   <div>
      <ContactSalesPopup
         @close="toggleContactSales"
         :contactSalesActive="contactSalesActive"
         class="overflow-y-hidden"
      >
      </ContactSalesPopup>
      <EmailGrabberPopup
         @close="toggleGrabber"
         :grabberActive="grabberActive"
         :freePlan="freePlan"
         :payPlan="payPlan"
         :planId="planId"
         class="overflow-y-hidden"
      >
      </EmailGrabberPopup>
      <!-- page meta -->
      <div>
         <Head>
            <title>Pricing Page</title>
            <link
               rel="icon"
               :href="$filters.public_path('favicon.png')"
               type="favicon"
            />
            <meta name="description" content="Awesome Pricing" />
         </Head>
      </div>
      <!-- content -->
      <div
         class="min-[1140px]:max-w-[1200px] container relative mx-auto bg-transparent"
      >
         <!-- heading & toggler -->
         <section
            id="section1"
            class="hero-section relative mx-auto mt-[150px] justify-center gap-1 bg-transparent flex lg:py-0"
            :style="backgroundImageStyleMain"
         >
            <div class="mt-0 bg-transparent px-[25px] xl:px-0 z-10">
               <div class="">
                  <!-- heading -->
                  <h1
                     class="top-full mt-0 text-center px-0 text-[35px] font-extrabold tracking-normal text-[#fff] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
                  >
                     The best member solution, for the best price.
                  </h1>
                  <!--  tooltip & toggler -->
                  <div
                     @mouseenter="showYearlySavings = !showYearlySavings"
                     @mouseleave="showYearlySavings = !showYearlySavings"
                     class="mt-[30px] w-[230px] h-[47px] relative rounded-full border border-white flex mx-auto"
                  >
                     <!-- yearly savings tooltip -->
                     <div
                        v-show="showYearlySavings"
                        class="absolute z-40 -translate-y-20 translate-x-32 ease-in-out duration-500"
                     >
                        <div
                           class="w-[107px] h-[66px] bg-white py-[9px] px-3 right-0"
                        >
                           <p
                              class="text-[13px] text-[#2d8bff] text-center leading-4"
                           >
                              Pay yearly and save up to 45%
                           </p>
                        </div>
                        <div
                           class="w-[5px] h-[5px] bg-white rotate-45 -mt-[2px] mx-auto"
                        ></div>
                     </div>
                     <!--  toggle year  -->
                     <div
                        @click="togglePricing(1)"
                        v-bind:class="{
                           'bg-transparent text-white': switchPricing !== 1,
                           'bg-white text-[#2d8bff] ': switchPricing === 1
                        }"
                        class="rounded-full text-center w-[115px] py-auto pt-[10px] cursor-pointer"
                     >
                        <a class="self-center font-semibold text-[15px] my-auto"
                           >Yearly</a
                        >
                     </div>
                     <!--  toggle month  -->
                     <div
                        @click="togglePricing(2)"
                        v-bind:class="{
                           'bg-transparent text-white': switchPricing !== 2,
                           'bg-white text-[#2d8bff] ': switchPricing === 2
                        }"
                        class="rounded-3xl text-center align-middle w-[115px] pt-[10px]"
                     >
                        <a
                           class="items-center font-semibold text-[15px] my-auto cursor-pointer"
                           >Monthly</a
                        >
                     </div>
                  </div>
               </div>
            </div>
         </section>
         <!-- pricing cards desktop -->
         <div class="hidden min-[830px]:block">
            <!-- annually -->
            <div
               class="flex mt-[30px] gap-[4px] z-20 mx-auto justify-center"
               v-if="switchPricing === 1"
            >
               <!-- cards -->
               <div
                  class="rounded-lg bg-white shadow-lg border-b-4 text-center px-auto pt-[55px] pb-[25px] w-[240px] z-20"
                  :class="{
                     'border-[#00b884]': plan.name === 'Free',
                     'border-[#49ccf9]': plan.name === 'Advanced',
                     'border-[#2d8bff]': plan.name === 'Pro',
                     'border-[#292D34]': plan.name === 'Enterprise'
                  }"
                  v-for="(plan, index) in annualPlans"
               >
                  <!-- name -->
                  <h3
                     class="text-lg text-[#00b884] font-semibold uppercase"
                     v-if="plan.name === 'Free'"
                  >
                     {{ plan.name }} Forever
                  </h3>
                  <h3
                     class="text-lg text-[#2d8bff] font-semibold uppercase"
                     v-else-if="plan.name === 'Pro'"
                  >
                     Professional
                  </h3>
                  <h3
                     class="text-lg font-semibold uppercase"
                     :class="{
                        'text-[#49ccf9]': plan.name === 'Advanced',
                        'text-[#292D34]': plan.name === 'Enterprise'
                     }"
                     v-else
                  >
                     {{ plan.name }}
                  </h3>
                  <!-- tag -->
                  <div>
                     <h4
                        v-if="plan.name === 'Enterprise'"
                        class="text-sm text-[#292D34]"
                     >
                        Best for large audiences
                     </h4>
                     <h4 v-else class="text-sm text-[#292D34]">
                        Best for personal use
                     </h4>
                  </div>
                  <!-- price -->
                  <div class="flex mx-auto justify-center">
                     <!-- $ -->
                     <h4
                        class="text-[25px] font-medium mt-[30px]"
                        :class="{
                           'text-[#49ccf9]': plan.name === 'Advanced',
                           'text-[#2d8bff]': plan.name === 'Pro'
                        }"
                        v-if="plan.name === 'Advanced' || plan.name === 'Pro'"
                     >
                        $
                     </h4>
                     <h4
                        class="text-[55px] font-medium text-[#00b884]"
                        :class="{
                           'text-[#49ccf9]': plan.name === 'Advanced',
                           'text-[#2d8bff]': plan.name === 'Pro'
                        }"
                        v-if="plan.name === 'Advanced' || plan.name === 'Pro'"
                     >
                        {{ formatNumber(plan.price / 12) }}
                     </h4>
                     <h4
                        class="text-[55px] font-medium text-[#00b884]"
                        v-else-if="plan.name === 'Free'"
                     >
                        Free
                     </h4>
                     <div class="flex mx-auto justify-center" v-else>
                        <img
                           class="w-[70px] mx-auto mt-[10px]"
                           :src="
                              $filters.media_path('images/enterprise-icon.png')
                           "
                           alt="enterprise"
                        />
                     </div>
                  </div>
                  <!-- type -->
                  <div>
                     <h4
                        class="text-sm font-semibold text-[#b9bec7] uppercase"
                        v-if="plan.name === 'Advanced' || plan.name === 'Pro'"
                     >
                        per month
                     </h4>
                     <h4
                        class="text-sm font-semibold text-[#b9bec7] uppercase"
                        v-else-if="plan.name === 'Free'"
                     >
                        forever
                     </h4>
                  </div>
                  <!-- actions -->
                  <!-- free -->
                  <button
                     v-if="plan.name === 'Free'"
                     @click="toggleGrabber('free', null)"
                     type="button"
                     class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#00b884] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#00b884]/25 transition ease-in hover:bg-[#1B964F] active:bg-[#054ca3] dark:border-none"
                  >
                     Free Forever
                  </button>
                  <!-- adv -->
                  <button
                     v-else-if="plan.name === 'Advanced'"
                     @click="toggleGrabber('pay', plan.id)"
                     type="button"
                     class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#49ccf9] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#49ccf9]/30 transition ease-in hover:bg-[#1EB7EC] active:bg-[#054ca3] dark:border-none"
                  >
                     {{
                        plan.trial_days
                           ? `Free ${plan.trial_days} Days Trial`
                           : 'Start Plan'
                     }}
                  </button>
                  <!-- pro -->
                  <button
                     v-else-if="plan.name === 'Pro'"
                     @click="toggleGrabber('pay', plan.id)"
                     type="button"
                     class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#2d8bff] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#2d8bff]/30 transition ease-in hover:bg-[#0f61c5] active:bg-[#054ca3] dark:border-none"
                  >
                     {{
                        plan.trial_days
                           ? `Free ${plan.trial_days} Days Trial`
                           : 'Start Plan'
                     }}
                  </button>
                  <!-- enterprise -->
                  <button
                     v-else-if="plan.name === 'Enterprise'"
                     type="button"
                     @click="toggleContactSales"
                     class="h-[56px] w-[200px] mt-[37px] mb-[20px] rounded-[9px] bg-[#292D34] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#292D34]/20 transition ease-in hover:bg-black active:bg-black dark:border-none"
                  >
                     Contact Sales
                  </button>
                  <!-- details -->
                  <ul class="text-center" v-html="plan.desc"></ul>
               </div>
            </div>
            <!-- monthly -->
            <div
               class="flex mt-[30px] gap-[4px] z-20 mx-auto justify-center"
               v-else
            >
               <!-- cards -->
               <div
                  class="rounded-lg bg-white shadow-lg border-b-4 text-center px-auto pt-[55px] pb-[25px] w-[240px] z-20"
                  :class="{
                     'border-[#00b884]': plan.name === 'Free',
                     'border-[#49ccf9]': plan.name === 'Advanced',
                     'border-[#2d8bff]': plan.name === 'Pro',
                     'border-[#292D34]': plan.name === 'Enterprise'
                  }"
                  v-for="(plan, index) in monthlyPlans"
               >
                  <!-- name -->
                  <h3
                     class="text-lg text-[#00b884] font-semibold uppercase"
                     v-if="plan.name === 'Free'"
                  >
                     {{ plan.name }} Forever
                  </h3>
                  <h3
                     class="text-lg text-[#2d8bff] font-semibold uppercase"
                     v-else-if="plan.name === 'Pro'"
                  >
                     Professional
                  </h3>
                  <h3
                     class="text-lg font-semibold uppercase"
                     :class="{
                        'text-[#49ccf9]': plan.name === 'Advanced',
                        'text-[#292D34]': plan.name === 'Enterprise'
                     }"
                     v-else
                  >
                     {{ plan.name }}
                  </h3>
                  <!-- tag -->
                  <div>
                     <h4
                        v-if="plan.name === 'Enterprise'"
                        class="text-sm text-[#292D34]"
                     >
                        Best for large audiences
                     </h4>
                     <h4 v-else class="text-sm text-[#292D34]">
                        Best for personal use
                     </h4>
                  </div>
                  <!-- price -->
                  <div class="flex mx-auto justify-center">
                     <!-- $ -->
                     <h4
                        class="text-[25px] font-medium mt-[30px]"
                        :class="{
                           'text-[#49ccf9]': plan.name === 'Advanced',
                           'text-[#2d8bff]': plan.name === 'Pro'
                        }"
                        v-if="plan.name === 'Advanced' || plan.name === 'Pro'"
                     >
                        $
                     </h4>
                     <h4
                        class="text-[55px] font-medium text-[#00b884]"
                        :class="{
                           'text-[#49ccf9]': plan.name === 'Advanced',
                           'text-[#2d8bff]': plan.name === 'Pro'
                        }"
                        v-if="plan.name === 'Advanced' || plan.name === 'Pro'"
                     >
                        {{ plan.price }}
                     </h4>
                     <h4
                        class="text-[55px] font-medium text-[#00b884]"
                        v-else-if="plan.name === 'Free'"
                     >
                        Free
                     </h4>
                     <div class="flex mx-auto justify-center" v-else>
                        <img
                           class="w-[70px] mx-auto mt-[10px]"
                           :src="
                              $filters.media_path('images/enterprise-icon.png')
                           "
                           alt="enterprise"
                        />
                     </div>
                  </div>
                  <!-- type -->
                  <div>
                     <h4
                        class="text-sm font-semibold text-[#b9bec7] uppercase"
                        v-if="plan.name === 'Advanced' || plan.name === 'Pro'"
                     >
                        per month
                     </h4>
                     <h4
                        class="text-sm font-semibold text-[#b9bec7] uppercase"
                        v-else-if="plan.name === 'Free'"
                     >
                        forever
                     </h4>
                  </div>
                  <!-- actions -->
                  <!-- free -->
                  <button
                     v-if="plan.name === 'Free'"
                     @click="toggleGrabber('free', null)"
                     type="button"
                     class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#00b884] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#00b884]/25 transition ease-in hover:bg-[#1B964F] active:bg-[#054ca3] dark:border-none"
                  >
                     Free Forever
                  </button>
                  <!-- adv -->
                  <button
                     v-else-if="plan.name === 'Advanced'"
                     @click="toggleGrabber('pay', plan.id)"
                     type="button"
                     class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#49ccf9] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#49ccf9]/30 transition ease-in hover:bg-[#1EB7EC] active:bg-[#054ca3] dark:border-none"
                  >
                     {{
                        plan.trial_days
                           ? `Free ${plan.trial_days} Days Trial`
                           : 'Start Plan'
                     }}
                  </button>
                  <!-- pro -->
                  <button
                     v-else-if="plan.name === 'Pro'"
                     @click="toggleGrabber('pay', plan.id)"
                     type="button"
                     class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#2d8bff] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#2d8bff]/30 transition ease-in hover:bg-[#0f61c5] active:bg-[#054ca3] dark:border-none"
                  >
                     {{
                        plan.trial_days
                           ? `Free ${plan.trial_days} Days Trial`
                           : 'Start Plan'
                     }}
                  </button>
                  <!-- enterprise -->
                  <button
                     v-else-if="plan.name === 'Enterprise'"
                     type="button"
                     @click="toggleContactSales"
                     class="h-[56px] w-[200px] mt-[37px] mb-[20px] rounded-[9px] bg-[#292D34] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#292D34]/20 transition ease-in hover:bg-black active:bg-black dark:border-none"
                  >
                     Contact Sales
                  </button>
                  <!-- details -->
                  <ul class="text-center" v-html="plan.desc"></ul>
               </div>
            </div>
         </div>
         <!-- pricing cards mobile -->
         <div class="min-[830px]:hidden z-10">
            <!-- anually -->
            <div
               class="z-10 flex mt-[30px] gap-[4px] mx-auto justify-center px-[25px]"
               v-if="switchPricing === 1"
            >
               <Swiper
                  :slidesPerView="'1'"
                  :spacesBetween="40"
                  :centeredSlides="true"
                  :navigation="{ clickable: true }"
                  :modules="modules"
                  class="mySwiper"
               >
                  <!--  free card -->
                  <SwiperSlide class="" v-for="(plan, index) in annualPlans">
                     <div
                        class="mx-auto rounded-lg bg-white shadow-lg border-b-4 h-[683px] w-[300px] sm:w-[500px] text-center px-auto pt-[55px] pb-[25px] z-10"
                        :class="{
                           'border-[#00b884]': plan.name === 'Free',
                           'border-[#49ccf9]': plan.name === 'Advanced',
                           'border-[#2d8bff]': plan.name === 'Pro',
                           'border-[#292D34]': plan.name === 'Enterprise'
                        }"
                     >
                        <!-- name -->
                        <h3
                           class="text-lg text-[#00b884] font-semibold uppercase"
                           v-if="plan.name === 'Free'"
                        >
                           {{ plan.name }} Forever
                        </h3>
                        <h3
                           class="text-lg text-[#2d8bff] font-semibold uppercase"
                           v-else-if="plan.name === 'Pro'"
                        >
                           Professional
                        </h3>
                        <h3
                           class="text-lg font-semibold uppercase"
                           :class="{
                              'text-[#49ccf9]': plan.name === 'Advanced',
                              'text-[#292D34]': plan.name === 'Enterprise'
                           }"
                           v-else
                        >
                           {{ plan.name }}
                        </h3>
                        <!-- tag -->
                        <div>
                           <h4
                              v-if="plan.name === 'Enterprise'"
                              class="text-sm text-[#292D34]"
                           >
                              Best for large audiences
                           </h4>
                           <h4 v-else class="text-sm text-[#292D34]">
                              Best for personal use
                           </h4>
                        </div>
                        <!-- price -->
                        <div class="flex mx-auto justify-center">
                           <!-- $ -->
                           <h4
                              class="text-[25px] font-medium mt-[30px]"
                              :class="{
                                 'text-[#49ccf9]': plan.name === 'Advanced',
                                 'text-[#2d8bff]': plan.name === 'Pro'
                              }"
                              v-if="
                                 plan.name === 'Advanced' || plan.name === 'Pro'
                              "
                           >
                              $
                           </h4>
                           <h4
                              class="text-[55px] font-medium text-[#00b884]"
                              :class="{
                                 'text-[#49ccf9]': plan.name === 'Advanced',
                                 'text-[#2d8bff]': plan.name === 'Pro'
                              }"
                              v-if="
                                 plan.name === 'Advanced' || plan.name === 'Pro'
                              "
                           >
                              {{ formatNumber(plan.price / 12) }}
                           </h4>
                           <h4
                              class="text-[55px] font-medium text-[#00b884]"
                              v-else-if="plan.name === 'Free'"
                           >
                              Free
                           </h4>
                           <div class="flex mx-auto justify-center" v-else>
                              <img
                                 class="w-[70px] mx-auto mt-[10px]"
                                 :src="
                                    $filters.media_path(
                                       'images/enterprise-icon.png'
                                    )
                                 "
                                 alt="enterprise"
                              />
                           </div>
                        </div>
                        <!-- type -->
                        <div>
                           <h4
                              class="text-sm font-semibold text-[#b9bec7] uppercase"
                              v-if="
                                 plan.name === 'Advanced' || plan.name === 'Pro'
                              "
                           >
                              per month
                           </h4>
                           <h4
                              class="text-sm font-semibold text-[#b9bec7] uppercase"
                              v-else-if="plan.name === 'Free'"
                           >
                              forever
                           </h4>
                        </div>
                        <!-- actions -->
                        <!-- free -->
                        <button
                           v-if="plan.name === 'Free'"
                           @click="toggleGrabber('free', null)"
                           type="button"
                           class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#00b884] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#00b884]/25 transition ease-in hover:bg-[#1B964F] active:bg-[#054ca3] dark:border-none"
                        >
                           Free Forever
                        </button>
                        <!-- adv -->
                        <button
                           v-else-if="plan.name === 'Advanced'"
                           @click="toggleGrabber('pay', plan.id)"
                           type="button"
                           class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#49ccf9] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#49ccf9]/30 transition ease-in hover:bg-[#1EB7EC] active:bg-[#054ca3] dark:border-none"
                        >
                           {{
                              plan.trial_days
                                 ? `Free ${plan.trial_days} Days Trial`
                                 : 'Start Plan'
                           }}
                        </button>
                        <!-- pro -->
                        <button
                           v-else-if="plan.name === 'Pro'"
                           @click="toggleGrabber('pay', plan.id)"
                           type="button"
                           class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#2d8bff] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#2d8bff]/30 transition ease-in hover:bg-[#0f61c5] active:bg-[#054ca3] dark:border-none"
                        >
                           {{
                              plan.trial_days
                                 ? `Free ${plan.trial_days} Days Trial`
                                 : 'Start Plan'
                           }}
                        </button>
                        <!-- enterprise -->
                        <button
                           v-else-if="plan.name === 'Enterprise'"
                           type="button"
                           @click="toggleContactSales"
                           class="h-[56px] w-[200px] mt-[37px] mb-[20px] rounded-[9px] bg-[#292D34] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#292D34]/20 transition ease-in hover:bg-black active:bg-black dark:border-none"
                        >
                           Contact Sales
                        </button>
                        <!-- details -->
                        <ul class="text-center" v-html="plan.desc"></ul>
                     </div>
                  </SwiperSlide>
               </Swiper>
            </div>
            <!-- monthly -->
            <div
               v-else
               class="z-10 flex mt-[30px] gap-[4px] mx-auto justify-center px-[25px]"
            >
               <Swiper
                  :slidesPerView="'1'"
                  :spacesBetween="40"
                  :centeredSlides="true"
                  :navigation="{ clickable: true }"
                  :modules="modules"
                  class="mySwiper"
               >
                  <!-- cards -->
                  <SwiperSlide class="" v-for="(plan, index) in monthlyPlans">
                     <div
                        class="mx-auto rounded-lg bg-white shadow-lg border-b-4 h-[683px] w-[300px] sm:w-[500px] border-[#00b884] text-center px-auto pt-[55px] pb-[25px] z-10"
                        :class="{
                           'border-[#00b884]': plan.name === 'Free',
                           'border-[#49ccf9]': plan.name === 'Advanced',
                           'border-[#2d8bff]': plan.name === 'Pro',
                           'border-[#292D34]': plan.name === 'Enterprise'
                        }"
                     >
                        <!-- name -->
                        <h3
                           class="text-lg text-[#00b884] font-semibold uppercase"
                           v-if="plan.name === 'Free'"
                        >
                           {{ plan.name }} Forever
                        </h3>
                        <h3
                           class="text-lg text-[#2d8bff] font-semibold uppercase"
                           v-else-if="plan.name === 'Pro'"
                        >
                           Professional
                        </h3>
                        <h3
                           class="text-lg font-semibold uppercase"
                           :class="{
                              'text-[#49ccf9]': plan.name === 'Advanced',
                              'text-[#292D34]': plan.name === 'Enterprise'
                           }"
                           v-else
                        >
                           {{ plan.name }}
                        </h3>
                        <!-- tag -->
                        <div>
                           <h4
                              v-if="plan.name === 'Enterprise'"
                              class="text-sm text-[#292D34]"
                           >
                              Best for large audiences
                           </h4>
                           <h4 v-else class="text-sm text-[#292D34]">
                              Best for personal use
                           </h4>
                        </div>
                        <!-- price -->
                        <div class="flex mx-auto justify-center">
                           <!-- $ -->
                           <h4
                              class="text-[25px] font-medium mt-[30px]"
                              :class="{
                                 'text-[#49ccf9]': plan.name === 'Advanced',
                                 'text-[#2d8bff]': plan.name === 'Pro'
                              }"
                              v-if="
                                 plan.name === 'Advanced' || plan.name === 'Pro'
                              "
                           >
                              $
                           </h4>
                           <h4
                              class="text-[55px] font-medium text-[#00b884]"
                              :class="{
                                 'text-[#49ccf9]': plan.name === 'Advanced',
                                 'text-[#2d8bff]': plan.name === 'Pro'
                              }"
                              v-if="
                                 plan.name === 'Advanced' || plan.name === 'Pro'
                              "
                           >
                              {{ plan.price }}
                           </h4>
                           <h4
                              class="text-[55px] font-medium text-[#00b884]"
                              v-else-if="plan.name === 'Free'"
                           >
                              Free
                           </h4>
                           <div class="flex mx-auto justify-center" v-else>
                              <img
                                 class="w-[70px] mx-auto mt-[10px]"
                                 :src="
                                    $filters.media_path(
                                       'images/enterprise-icon.png'
                                    )
                                 "
                                 alt="enterprise"
                              />
                           </div>
                        </div>
                        <!-- type -->
                        <div>
                           <h4
                              class="text-sm font-semibold text-[#b9bec7] uppercase"
                              v-if="
                                 plan.name === 'Advanced' || plan.name === 'Pro'
                              "
                           >
                              per month
                           </h4>
                           <h4
                              class="text-sm font-semibold text-[#b9bec7] uppercase"
                              v-else-if="plan.name === 'Free'"
                           >
                              forever
                           </h4>
                        </div>
                        <!-- actions -->
                        <!-- free -->
                        <button
                           v-if="plan.name === 'Free'"
                           @click="toggleGrabber('free', null)"
                           type="button"
                           class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#00b884] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#00b884]/25 transition ease-in hover:bg-[#1B964F] active:bg-[#054ca3] dark:border-none"
                        >
                           Free Forever
                        </button>
                        <!-- adv -->
                        <button
                           v-else-if="plan.name === 'Advanced'"
                           @click="toggleGrabber('pay', plan.id)"
                           type="button"
                           class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#49ccf9] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#49ccf9]/30 transition ease-in hover:bg-[#1EB7EC] active:bg-[#054ca3] dark:border-none"
                        >
                           {{
                              plan.trial_days
                                 ? `Free ${plan.trial_days} Days Trial`
                                 : 'Start Plan'
                           }}
                        </button>
                        <!-- pro -->
                        <button
                           v-else-if="plan.name === 'Pro'"
                           @click="toggleGrabber('pay', plan.id)"
                           type="button"
                           class="h-[56px] w-[200px] mt-[25px] mb-[20px] rounded-[9px] bg-[#2d8bff] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#2d8bff]/30 transition ease-in hover:bg-[#0f61c5] active:bg-[#054ca3] dark:border-none"
                        >
                           {{
                              plan.trial_days
                                 ? `Free ${plan.trial_days} Days Trial`
                                 : 'Start Plan'
                           }}
                        </button>
                        <!-- enterprise -->
                        <button
                           v-else-if="plan.name === 'Enterprise'"
                           type="button"
                           @click="toggleContactSales"
                           class="h-[56px] w-[200px] mt-[37px] mb-[20px] rounded-[9px] bg-[#292D34] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#292D34]/20 transition ease-in hover:bg-black active:bg-black dark:border-none"
                        >
                           Contact Sales
                        </button>
                        <!-- details -->
                        <ul class="text-center" v-html="plan.desc"></ul>
                     </div>
                  </SwiperSlide>
               </Swiper>
            </div>
         </div>
         <!-- feature list -->
         <section class="mt-[30px] w-full justify-center text-center">
            <div class="mx-auto w-full justify-center">
               <button
                  @click="revealFeatureList = !revealFeatureList"
                  type="button"
                  class="rounded-full relative w-[260px] h-[60px] bg-white border text-left pl-[18px] text-[#2d8bff] text-lg py-auto border-[#2d8bff] mx-auto shadow-xl shadow-[#2d8bff]/10"
               >
                  Complete Feature List
                  <img
                     v-show="revealFeatureList"
                     class="absolute w-[20px] h-[20px] right-0 mr-[18px] top-0 mt-[18px]"
                     :src="$filters.media_path('images/chevron-up-blue.svg')"
                     alt="chevron-up-blue"
                  />
                  <img
                     v-show="!revealFeatureList"
                     class="absolute w-[20px] h-[20px] right-0 mr-[18px] top-0 mt-[18px]"
                     :src="$filters.media_path('images/chevron-down-blue.svg')"
                     alt="chevron-up-blue"
                  />
               </button>
            </div>
         </section>
         <!-- feature list tab - membership -->
         <section
            v-show="revealFeatureList"
            class="px-[10px] min-[1200px]:px-0 mt-[70px]"
         >
            <!-- table desktop -->
            <div
               class="px-[25px] lg:px-0 hidden min-[1100px]:block"
               v-for="(table, indexTable) in tables"
               :key="indexTable"
               :id="table.id"
            >
               <table class="lg:w-[1140px] mx-auto">
                  <!-- table header -->
                  <thead
                     v-bind:class="{
                        '': !tableHeader[0].toggle,
                        'feature-list-one-header': tableHeader[0].toggle
                     }"
                     class="bg-white rounded-lg lg:w-[1140px] z-10"
                  >
                     <!-- tab - header -->
                     <tr class="w-full">
                        <th
                           v-for="(header, index) in tableHeader"
                           :key="index"
                           :class="{
                              'w-[395px]':
                                 header.isSpecial &&
                                 tableVisibility[indexTable],
                              'w-full':
                                 header.isSpecial &&
                                 !tableVisibility[indexTable],
                              'w-[186px]': !header.isSpecial
                           }"
                        >
                           <!-- toggler -->
                           <div
                              class="flex justify-between"
                              v-if="header.isSpecial"
                           >
                              <!-- icon & name -->
                              <div
                                 v-bind:class="{
                                    'pt-[20px] pb-[28px] ':
                                       !tableVisibility[indexTable],
                                    'pt-[30px] pb-[38px] ':
                                       tableVisibility[indexTable]
                                 }"
                                 class="flex pl-[35px] align-middle gap-3"
                              >
                                 <!-- icon -->
                                 <div
                                    @click="toggleTable(indexTable)"
                                    v-bind:class="{
                                       'bg-[#f8f9fb]':
                                          !tableVisibility[indexTable],
                                       'bg-[#2d8bff]':
                                          tableVisibility[indexTable]
                                    }"
                                    class="w-[50px] h-[50px] rounded-lg shadow-lg mt-[30px] pt-[17px] cursor-pointer"
                                 >
                                    <svg
                                       width="26"
                                       height="15"
                                       viewBox="0 0 26 15"
                                       fill="none"
                                       xmlns="http://www.w3.org/2000/svg"
                                       class="mx-auto"
                                    >
                                       <path
                                          class="mt-[20px]"
                                          v-show="!tableVisibility[indexTable]"
                                          d="M24 2L13.7071 12.2929C13.3166 12.6834 12.6834 12.6834 12.2929 12.2929L2 2"
                                          stroke="#B9BEC7"
                                          stroke-width="3"
                                          stroke-linecap="round"
                                       ></path>

                                       <path
                                          v-show="tableVisibility[indexTable]"
                                          d="M2 13L12.2929 2.70711C12.6834 2.31658 13.3166 2.31658 13.7071 2.70711L24 13"
                                          stroke="white"
                                          stroke-width="3"
                                          stroke-linecap="round"
                                       ></path>
                                    </svg>
                                 </div>
                                 <!-- title -->
                                 <h2
                                    v-bind:class="{
                                       'text-[25px] text-[#C7C7C7]':
                                          !tableVisibility[indexTable],
                                       ' text-[35px]':
                                          tableVisibility[indexTable]
                                    }"
                                    class="mt-[20px] px-0 font-bold text-[#292d34] lg:max-w-[600px] lg:leading-[69px]"
                                 >
                                    {{ table.header }}
                                 </h2>
                              </div>
                              <!-- feature counts -->
                              <div
                                 v-show="!tableVisibility[indexTable]"
                                 class="w-[90px] h-[26px] rounded-full text-center border border-[#C7C7C7] my-auto mr-[35px]"
                              >
                                 <p
                                    class="text-[#C7C7C7] text-[11px] my-auto mt-[4px]"
                                 >
                                    {{ table.feature_count }}
                                 </p>
                              </div>
                           </div>
                           <!-- tiers -->
                           <div
                              v-else-if="tableVisibility[indexTable]"
                              class="w-[150px] text-center pt-[30px] pb-[38px] mx-auto"
                           >
                              <h3
                                 class="pb-[15px] mx-auto text-[13px] font-semibold"
                              >
                                 {{ header.title }}
                              </h3>

                              <button
                                 type="button"
                                 class="w-[132px] mx-auto h-[50px] text-[13px] font-semibold border rounded-lg py-auto shadow-lg hover:ease-in-out hover:duration-500 hover:text-white"
                                 :class="{
                                    'border-[#00b884] text-[#00b884] shadow-[#00b884]/10 hover:bg-[#00b884]':
                                       header.title === 'FREE',
                                    'border-[#49ccf9] text-[#49ccf9] shadow-[#49ccf9]/10 hover:bg-[#49ccf9]':
                                       header.title === 'ADVANCED',
                                    'border-[#2d8bff] text-[#2d8bff] shadow-[#2d8bff]/10 hover:bg-[#2d8bff]':
                                       header.title === 'PROFESSIONAL',
                                    'border-[#292D34] text-[#292D34] shadow-[#292D34]/10 hover:bg-[#292D34]':
                                       header.title === 'ENTERPRISE'
                                 }"
                                 @click="
                                    header.tier === 'free'
                                       ? toggleGrabber('free', null)
                                       : header.tier === 'pay'
                                         ? toggleGrabber('pay', header.planId)
                                         : header.tier === 'sale'
                                           ? toggleContactSales()
                                           : toggleGrabber
                                 "
                              >
                                 {{ header.buttonText }}
                              </button>
                           </div>
                        </th>
                     </tr>
                  </thead>
                  <!-- table floating header -->
                  <thead
                     class="bg-white rounded-lg lg:w-[1140px] z-10 hidden"
                     :class="{
                        hidden: !tableVisibility[indexTable],
                        ['feature-list-' + table.id + '-header-floater']:
                           tableVisibility[indexTable]
                     }"
                  >
                     <!-- tab - header -->
                     <tr class="w-full">
                        <th
                           v-for="(header, index) in tableHeader"
                           :key="index"
                           :class="{
                              'w-[395px]':
                                 header.isSpecial &&
                                 tableVisibility[indexTable],
                              'w-full':
                                 header.isSpecial &&
                                 !tableVisibility[indexTable],
                              'w-[186px]': !header.isSpecial
                           }"
                        >
                           <!-- toggler -->
                           <div
                              class="flex justify-between"
                              v-if="header.isSpecial"
                           >
                              <div
                                 v-bind:class="{
                                    'pt-[20px] pb-[28px] ':
                                       !tableVisibility[indexTable],
                                    'pt-[30px] pb-[38px] ':
                                       tableVisibility[indexTable]
                                 }"
                                 class="flex pl-[35px] align-middle gap-3"
                              >
                                 <div
                                    @click="toggleTable(indexTable)"
                                    v-bind:class="{
                                       'bg-[#f8f9fb]':
                                          !tableVisibility[indexTable],
                                       'bg-[#2d8bff]':
                                          tableVisibility[indexTable]
                                    }"
                                    class="w-[50px] h-[50px] rounded-lg shadow-lg mt-[30px] pt-[17px] cursor-pointer"
                                 >
                                    <svg
                                       width="26"
                                       height="15"
                                       viewBox="0 0 26 15"
                                       fill="none"
                                       xmlns="http://www.w3.org/2000/svg"
                                       class="mx-auto"
                                    >
                                       <path
                                          class="mt-[20px]"
                                          v-show="!tableVisibility[indexTable]"
                                          d="M24 2L13.7071 12.2929C13.3166 12.6834 12.6834 12.6834 12.2929 12.2929L2 2"
                                          stroke="#B9BEC7"
                                          stroke-width="3"
                                          stroke-linecap="round"
                                       ></path>

                                       <path
                                          v-show="tableVisibility[indexTable]"
                                          d="M2 13L12.2929 2.70711C12.6834 2.31658 13.3166 2.31658 13.7071 2.70711L24 13"
                                          stroke="white"
                                          stroke-width="3"
                                          stroke-linecap="round"
                                       ></path>
                                    </svg>
                                 </div>
                                 <h2
                                    v-bind:class="{
                                       'text-[25px] text-[#C7C7C7]':
                                          !tableVisibility[indexTable],
                                       ' text-[35px]':
                                          tableVisibility[indexTable]
                                    }"
                                    class="mt-[20px] px-0 font-bold text-[#292d34] lg:max-w-[600px] lg:leading-[69px]"
                                 >
                                    {{ table.header }}
                                 </h2>
                              </div>
                              <div
                                 v-show="!tableVisibility[indexTable]"
                                 class="w-[90px] h-[26px] rounded-full text-center border border-[#C7C7C7] my-auto mr-[35px]"
                              >
                                 <p
                                    class="text-[#C7C7C7] text-[11px] my-auto mt-[4px]"
                                 >
                                    {{ table.feature_count }}
                                 </p>
                              </div>
                           </div>
                           <!-- tiers -->
                           <div
                              v-else-if="tableVisibility[indexTable]"
                              class="w-[150px] text-center pt-[30px] pb-[38px] mx-auto"
                           >
                              <h3
                                 class="pb-[15px] mx-auto text-[13px] font-semibold"
                              >
                                 {{ header.title }}
                              </h3>
                              <button
                                 type="button"
                                 class="w-[132px] mx-auto h-[50px] text-[13px] font-semibold border rounded-lg py-auto shadow-lg hover:ease-in-out hover:duration-500 hover:text-white"
                                 :class="{
                                    'border-[#00b884] text-[#00b884] shadow-[#00b884]/10 hover:bg-[#00b884]':
                                       header.title === 'FREE',
                                    'border-[#49ccf9] text-[#49ccf9] shadow-[#49ccf9]/10 hover:bg-[#49ccf9]':
                                       header.title === 'ADVANCED',
                                    'border-[#2d8bff] text-[#2d8bff] shadow-[#2d8bff]/10 hover:bg-[#2d8bff]':
                                       header.title === 'PROFESSIONAL',
                                    'border-[#292D34] text-[#292D34] shadow-[#292D34]/10 hover:bg-[#292D34]':
                                       header.title === 'ENTERPRISE'
                                 }"
                                 @click="
                                    header.tier === 'free'
                                       ? toggleGrabber('free', null)
                                       : header.tier === 'pay'
                                         ? toggleGrabber('pay', header.planId)
                                         : header.tier === 'sale'
                                           ? toggleContactSales()
                                           : toggleGrabber
                                 "
                              >
                                 {{ header.buttonText }}
                              </button>
                              <!-- </div> -->
                           </div>
                        </th>
                     </tr>
                  </thead>
                  <!-- table body -->
                  <tbody v-show="tableVisibility[indexTable]">
                     <tr
                        v-for="(row, rowIndex) in table.rows"
                        :key="`row-${rowIndex}`"
                        :class="{
                           'bg-[#a0a3af10] rounded-tl-[10px] rounded-bl-[10px]':
                              rowIndex % 2 === 0,
                           'table2End testing':
                              rowIndex === table.rows.length - 1
                        }"
                     >
                        <td
                           class="rounded-tl-[10px] rounded-bl-[10px] pl-[15px] text-sm font-bold text-[#292d34] lg:pl-[25px]"
                        >
                           {{ row.title }}
                        </td>
                        <td
                           v-for="(column, columnIndex) in row.columns"
                           :key="`column-${columnIndex}`"
                           :class="{
                              'w-[186px] rounded-tr-[10px] rounded-br-[10px] h-[78px]':
                                 column.type === 'img',
                              'py-[18px] text-center text-sm h-[78px]':
                                 column.type === 'text'
                           }"
                        >
                           <img
                              v-if="column.type === 'img'"
                              :src="$filters.media_path(column.src)"
                              alt=""
                              class="mx-auto w-[30px] lg:w-[40px]"
                              :class="{
                                 'drop-shadow-[0px_10px_10px_#83E5AC]':
                                    column.src.includes('green')
                              }"
                           />
                           <span v-if="column.type === 'text'">{{
                              column.content
                           }}</span>
                        </td>
                     </tr>
                  </tbody>
               </table>
            </div>
            <!-- table mobile -->
            <Swiper
               :slidesPerView="'1'"
               :spacesBetween="40"
               :centeredSlides="true"
               :navigation="{ clickable: true }"
               :modules="modules"
               class="mySwiper min-[1100px]:hidden"
            >
               <SwiperSlide
                  class=""
                  v-for="(header, index) in tableHeader.slice(1)"
                  :key="index"
               >
                  <div
                     class="min-[1100px]:hidden px-[18px] sm:px-[30px] md:px-[40px]"
                  >
                     <!-- heading & button -->
                     <div class="text-center">
                        <h3
                           class="text-lg font-semibold mx-auto"
                           :class="[header.textColor]"
                        >
                           {{ header.title }}
                        </h3>
                        <button
                           type="button"
                           class="h-[56px] w-[200px] mx-auto mt-[25px] mb-[20px] rounded-[9px] text-[14px] text-sm font-bold text-white shadow-xl transition ease-in dark:border-none"
                           :class="{
                              'bg-[#00b884] shadow-[#00b884]/25 hover:bg-[#1B964F] active:bg-[#054ca3]':
                                 header.title === 'FREE',
                              'bg-[#49ccf9] shadow-[#49ccf9]/40 hover:bg-[#1EB7EC] active:bg-[#054ca3]':
                                 header.title === 'ADVANCED',
                              'bg-[#2d8bff] shadow-[#2d8bff]/30 hover:bg-[#0f61c5] active:bg-[#054ca3]':
                                 header.title === 'PROFESSIONAL',
                              'bg-[#292D34] shadow-[#292D34]/20 hover:bg-[#0f61c5] active:bg-[#054ca3]':
                                 header.title === 'ENTERPRISE'
                           }"
                           @click="
                              header.tier === 'free'
                                 ? toggleGrabber('free', null)
                                 : header.tier === 'pay'
                                   ? toggleGrabber('pay', header.planId)
                                   : header.tier === 'sale'
                                     ? toggleContactSales()
                                     : toggleGrabber
                           "
                        >
                           {{
                              header.title === 'FREE'
                                 ? 'Free Forver'
                                 : header.title === 'ADVANCED'
                                   ? 'Get Started'
                                   : header.title === 'PROFESSIONAL'
                                     ? 'Get Started'
                                     : header.title === 'ENTERPRISE'
                                       ? 'Contact Sales'
                                       : 'Default text'
                           }}
                        </button>
                     </div>
                     <!-- table -->
                     <div
                        v-for="(table, indexTable) in header.rows"
                        :key="indexTable"
                     >
                        <table class="mx-auto w-full">
                           <!-- table header -->
                           <thead
                              v-bind:class="{
                                 '': !tableHeader[0].toggle,
                                 'feature-list-one-header':
                                    tableHeader[0].toggle
                              }"
                              class="bg-white rounded-lg w-full z-10"
                           >
                              <tr class="w-full">
                                 <!-- name -->
                                 <th class="w-full rounded-tl-lg">
                                    <div class="flex justify-between w-full">
                                       <div
                                          v-bind:class="{
                                             'pt-[20px] pb-[28px] ':
                                                !tableVisibility[indexTable],
                                             'pt-[30px] pb-[38px] ':
                                                tableVisibility[indexTable]
                                          }"
                                          class="flex pl-[20px] align-middle gap-3"
                                       >
                                          <h2
                                             v-bind:class="{
                                                'text-[25px] text-[#C7C7C7] mt-[35px]':
                                                   !tableVisibility[indexTable],
                                                ' text-[35px] mt-[25px]':
                                                   tableVisibility[indexTable]
                                             }"
                                             class="px-0 font-bold text-[#292d34]"
                                          >
                                             {{ table.title }}
                                          </h2>
                                       </div>
                                    </div>
                                 </th>
                                 <!-- toggler -->
                                 <th
                                    class="w-[100px] px-[15px] ml-[30px] rounded-tr-lg"
                                 >
                                    <div
                                       class="w-[50px] h-[50px] rounded-lg shadow-lg mt-[30px] pt-[17px] cursor-pointer"
                                       :class="{
                                          'bg-[#f8f9fb]':
                                             !tableVisibility[indexTable],
                                          'bg-[#00b884]':
                                             tableVisibility[indexTable] &&
                                             header.title === 'FREE',
                                          'bg-[#49ccf9]':
                                             tableVisibility[indexTable] &&
                                             header.title === 'ADVANCED',
                                          'bg-[#2d8bff]':
                                             tableVisibility[indexTable] &&
                                             header.title === 'PROFESSIONAL',
                                          'bg-[#292D34]':
                                             tableVisibility[indexTable] &&
                                             header.title === 'ENTERPRISE'
                                       }"
                                       @click="toggleTable(indexTable)"
                                    >
                                       <svg
                                          width="26"
                                          height="15"
                                          viewBox="0 0 26 15"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                          class="mx-auto"
                                       >
                                          <path
                                             class="mt-[20px]"
                                             v-show="
                                                !tableVisibility[indexTable]
                                             "
                                             d="M24 2L13.7071 12.2929C13.3166 12.6834 12.6834 12.6834 12.2929 12.2929L2 2"
                                             stroke="#B9BEC7"
                                             stroke-width="3"
                                             stroke-linecap="round"
                                          ></path>
                                          <path
                                             v-show="
                                                tableVisibility[indexTable]
                                             "
                                             d="M2 13L12.2929 2.70711C12.6834 2.31658 13.3166 2.31658 13.7071 2.70711L24 13"
                                             stroke="white"
                                             stroke-width="3"
                                             stroke-linecap="round"
                                          ></path>
                                       </svg>
                                    </div>
                                 </th>
                              </tr>
                           </thead>
                           <!-- table body -->
                           <tbody v-show="tableVisibility[indexTable]">
                              <tr
                                 v-for="(column, columnIndex) in table.columns"
                                 :key="`column-${columnIndex}`"
                                 class="rounded-tl-[10px] rounded-bl-[10px]"
                                 :class="{
                                    'bg-[#a0a3af10]': columnIndex % 2 === 0
                                 }"
                              >
                                 <td
                                    class="w-[395px] rounded-tl-[10px] rounded-bl-[10px] pl-[15px] text-sm font-bold text-[#292d34] lg:pl-[25px]"
                                 >
                                    {{ column.name }}
                                 </td>
                                 <td
                                    class="w-[186px] rounded-tr-[10px] rounded-br-[10px] h-[78px] text-center"
                                 >
                                    <img
                                       v-if="column.type === 'img'"
                                       :src="$filters.media_path(column.src)"
                                       alt=""
                                       class="mx-auto w-[30px] lg:w-[40px]"
                                       :class="{
                                          'drop-shadow-[0px_10px_10px_#83E5AC]':
                                             column.src.includes('green')
                                       }"
                                    />
                                    <span v-if="column.type === 'text'">{{
                                       column.text
                                    }}</span>
                                 </td>
                              </tr>
                           </tbody>
                        </table>
                     </div>
                  </div>
               </SwiperSlide>
            </Swiper>
         </section>
         <!-- testimonial -->
         <section
            class="testimonial-blue-quotes mx-auto mt-[50px] lg:mt-[100px] max-w-[1200px] px-[25px] text-center xl:px-0"
         >
            <div class="py-10 px-[25px] lg:mx-auto lg:flex-col">
               <h3
                  class="mx-0 text-center text-xs font-extrabold text-[#2D8CFF]"
               >
                  TESTIMONIALS
               </h3>

               <h2
                  class="top-full mt-[20px] px-0 text-center text-[35px] font-bold text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
               >
                  See why Platforma is the future of courses.
               </h2>
            </div>
            <CarouselTestimonialBlueQuotes
               :pagination="true"
               :timeout="6000"
               class="carousel xl:w-[1100px] md:h-[450px] h-[350px] pt-[50px] bg-none relative mx-auto my-auto py-0"
               v-slot="{ currentSlide }"
            >
               <SlideBlueQuotesTestimonials
                  v-for="(slideBlueQuotesTestimonials, index) in carouselSlides"
                  :key="index"
               >
                  <div
                     v-show="currentSlide == index + 1"
                     class="slide-info absolute mx-auto bg-none w-full h-full rounded-[10px] sm:rounded-[60px] cursor-grabbing"
                  >
                     <img
                        :src="
                           $filters.media_path(
                              `/images/${slideBlueQuotesTestimonials}.png`
                           )
                        "
                        alt="Build your own podcast delivery website on your own domain to make more revenue from your Podcast."
                        class="mx-auto md:w-[800px] shadow-2xl"
                     />
                  </div>
               </SlideBlueQuotesTestimonials>
            </CarouselTestimonialBlueQuotes>
         </section>
         <!-- faq section -->
         <section
            class="mx-auto mt-[50px] lg:mt-[100px] max-w-[1200px] px-[25px] text-center xl:px-0"
         >
            <!-- faq headlines -->
            <div class="py-10 px-[25px] lg:mx-auto lg:flex-col">
               <h3
                  class="mx-0 text-center text-xs font-extrabold text-[#2D8CFF]"
               >
                  FAQ
               </h3>

               <h2
                  class="lg:top-full mt-[20px] px-0 text-center text-[26px] font-bold text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
               >
                  You've got questions. We've got answers.
               </h2>
            </div>
            <!-- faq matrix -->
            <div class="px-[10px] min-[540px]:px-[30px]">
               <!-- faqs  -->
               <div
                  class="w-full bg-white rounded-l-md rounded-r-lg py-[35px] px-[35px] text-left text-[#292d34] drop-shadow-[0_35px_35px_rgba(0,0,0,0.05)] mb-[15px]"
                  :class="{
                     'pl-[41px] ': !faqVisibility[index],
                     'border-l-[6px] border-[#2D8CFF]': faqVisibility[index]
                  }"
                  v-for="(item, index) in limitedItems"
                  :key="index"
               >
                  <!-- question & toggler -->
                  <div class="md:flex justify-between">
                     <!-- question -->
                     <h4
                        @click="toggleFaq(index)"
                        class="text-[25px] font-semibold cursor-pointer"
                     >
                        {{ item.question }}
                     </h4>
                     <!-- toggler -->
                     <svg
                        @click="toggleFaq(index)"
                        width="36"
                        height="37"
                        viewBox="0 0 36 37"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="cursor-pointer mx-auto md:mr-0 mt-2 md:mt-0"
                     >
                        <path
                           v-if="!faqVisibility[index]"
                           d="M2 18.25H34"
                           stroke="#E9EBF0"
                           stroke-width="4"
                           stroke-linecap="round"
                        ></path>
                        <path
                           v-if="faqVisibility[index]"
                           d="M2 18.25H34"
                           stroke="#2D8CFF"
                           stroke-width="4"
                           stroke-linecap="round"
                        ></path>
                        <path
                           v-if="!faqVisibility[index]"
                           d="M18 2.25L18 34.25"
                           stroke="#E9EBF0"
                           stroke-width="4"
                           stroke-linecap="round"
                        ></path>
                     </svg>
                  </div>
                  <!-- answer -->
                  <h5
                     v-if="faqVisibility[index]"
                     class="text-[16px] font-medium mt-[10px] text-[#7c828d]"
                  >
                     {{ item.answer }}
                  </h5>
               </div>
            </div>
            <!-- load more faq button -->
            <button
               @click="showMore"
               v-if="showMoreButton"
               type="button"
               class="h-[50px] w-[157px] mt-[45px] mb-[20px] rounded-[9px] bg-[#2d8bff] text-[14px] text-sm font-bold text-white shadow-xl shadow-[#2d8bff]/30 transition ease-in hover:bg-[#0f61c5] active:bg-[#054ca3] dark:border-none mx-auto"
            >
               Load More
            </button>
         </section>
      </div>
      <EmailIntegrationsSlider />
      <CtaBlock cta-headline="We're here to help." />
   </div>
</template>

<script>
import DefaultLayout from '../Layouts/DefaultLayout.vue';
export default {
   layout: DefaultLayout
};
</script>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import ButtonComponent from '@shared/Components/ButtonComponent.vue';
import EmailCaptureHeaderWidget from '../Components/EmailCaptureHeader.vue';
import EmailIntegrationsSlider from '../Components/EmailIntegrationsSlider.vue';
import CtaBlock from '../Components/CtaBlockFooterComponent.vue';
import CarouselTestimonialBlueQuotes from '../Components/CarouselTestimonialBlueQuotes.vue';
import SlideBlueQuotesTestimonials from '../Components/SlideBlueQuotesTestimonials.vue';
import EmailGrabberPopup from '../Components/EmailGrabberPopup.vue';
import ContactSalesPopup from '../Components/ContactSalesPopup.vue';
import FeatureData from '../feature_data.json';
import FaqData from '../faq_data.json';
import Axios from 'axios';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination, Navigation } from 'swiper';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css';

const openTab = ref(0);
const close = ref();
const freePlan = ref(false);
const payPlan = ref(false);
const planId = ref(null);
const switchPricing = ref(1);
const revealFeatureList = ref(false);
const showYearlySavings = ref(false);
const contactSalesActive = ref(false);
const grabberActive = ref(false);
const tables = FeatureData.tables;
const tableHeader = FeatureData.headers;
const tableVisibility = ref(new Array(tables.length).fill(true));
const faqItems = FaqData.faqs;
const faqVisibility = ref(new Array(faqItems.length).fill(false));
const modules = [Pagination, Navigation];
const itemsToShow = ref(4);
const order = ['Free', 'Advanced', 'Pro', 'Enterprise'];
const showMoreButton = computed(() => {
   return itemsToShow.value < faqItems.length;
});
const carouselSlides = [
   'testimonial-blue-1-vs-teachable',
   'testimonial-blue-2-vs-teachable',
   'testimonial-blue-3-vs-teachable'
];
// const toggleMobileTiers = ref(1);

const props = defineProps({
   plans: {
      type: Object,
      required: false
      // default: () => ({})
   }
});

const limitedItems = computed(() => {
   return faqItems.slice(0, itemsToShow.value);
});

const showMore = () => {
   itemsToShow.value += 10; // adjust this value to control how many items to load each time
};

const toggleFaq = (index) => {
   faqVisibility.value[index] = !faqVisibility.value[index];
};

const toggleTable = (index) => {
   tableVisibility.value[index] = !tableVisibility.value[index];
};

const toggleContactSales = () => {
   contactSalesActive.value = !contactSalesActive.value;
};

const toggleGrabber = (plan, id) => {
   if (plan === 'free') {
      freePlan.value = true;
      payPlan.value = false;
      planId.value = null;
   }
   if (plan === 'pay') {
      payPlan.value = true;
      planId.value = id;
      freePlan.value = false;
   }
   grabberActive.value = !grabberActive.value;
};

const togglePricing = function (pricingOptions) {
   switchPricing.value = pricingOptions;
};

const formatNumber = (num) => {
   return Number.isInteger(num) ? num : num.toFixed(2);
};

const monthlyPlans = computed(() =>
   props.plans
      .filter(
         (plan) =>
            (plan.type === 'monthly' || plan.type === 'free') &&
            plan.code !== 'starter-monthly'
      )
      .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name))
);

const annualPlans = computed(() =>
   props.plans
      .filter(
         (plan) =>
            (plan.type === 'annually' || plan.type === 'free') &&
            plan.code !== 'starter-annually'
      )
      .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name))
);

const createObserver = (tableElement, floaterElementSelector) => {
   const options = {
      rootMargin: '0px 0px -90% 0px',
      threshold: 0
   };
   const floaterElement = document.querySelector(floaterElementSelector);
   const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
         if (!entry.isIntersecting) {
            floaterElement.classList.add('hidden');
            floaterElement.classList.remove(
               'block',
               'fixed',
               'top-[100px]',
               'shadow-lg'
            );
         } else {
            floaterElement.classList.remove('hidden');
            floaterElement.classList.add(
               'block',
               'fixed',
               'top-[100px]',
               'shadow-lg'
            );
         }
      });
   }, options);

   observer.observe(tableElement);
};

onMounted(() => {
   tables.forEach((table) => {
      let tableElement = document.querySelector(`#${table.id}`);
      let floaterElementSelector = `.feature-list-${table.id}-header-floater`;
      createObserver(tableElement, floaterElementSelector);
   });
});

const backgroundImageStyleMain = computed(() => {
   const imagePath = '../../../../media/images/download-platforma-hero-bg.svg';
   return {
      '--before-background-image': `url(${imagePath})`
   };
});

// const toggleMobileTiers = function (mobileTiers) {
//   this.toggleMobileTiers = mobileTiers;
// };
// testimonial carousel
</script>

<style scoped>
.hero-section::before {
   content: '';
   position: absolute;
   top: 0;
   height: 1000px;
   width: 1700px;
   display: block;
   z-index: 0;
   transform: translateY(-16%) translateX(0%);
   background-repeat: no-repeat;
   background-image: var(--before-background-image);
}

.join-mobile {
   background-image: url(../media/images/two-dots.svg);
   background-size: contain;
}

.testimonial-blue-quotes {
   background-image: url(../media/images/two-dots.svg);
}

/* swiper styles */
.swiper-pagination-bullet {
   width: 20px;
   height: 20px;
}

.swiper-pagination-bullet-active {
   background-color: aqua;
}
</style>
